import { useState, useEffect } from "react";
import "./BasicDetails.css";
import { useSelector } from "react-redux";
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import { useNavigate } from "react-router-dom";
import ProfileCompletenessCard from "../ProfileCompletenessCard/ProfileCompletenessCard";

function BasicDetails() {
    const userDetails = useSelector((state) => state.userDetails.userDetails);
    const navigate = useNavigate();
    const [imageError, setImageError] = useState(false);

    useEffect(() => {
        setImageError(false);
    }, [userDetails?.photos]);

    const handleEditClick = () => {
        navigate("/profile-details/edit-cover-photo");
    }
    const handleEditNameClick = () => {
        if (userDetails?.governmentId === null) {
            navigate("/profile-details/edit-name");
        } else {
            navigate("/profile-details/request-status");
        }
    }

    const getUserInitials = (firstName, lastName) => {
        const firstInitial = firstName ? firstName.charAt(0).toUpperCase() : '';
        const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : '';
        return firstInitial + lastInitial;
    }

    const getBackgroundColor = (initials) => {
        const colors = [
            "#FF6B6B",
            "#4ECDC4",
            "#45B7D1",
            "#96CEB4",
            "#FFEAA7",
            "#DDA0DD",
            "#98D8C8",
            "#F7DC6F",
            "#BB8FCE",
            "#85C1E9",
            "#F8C471",
            "#82E0AA",
        ];
        if (!initials) return '#142424';
        let hash = 0;
        for (let i = 0; i < initials.length; i++) {
            hash = initials.charCodeAt(i) + ((hash << 5) - hash);
        }
        hash = Math.abs(hash);
        return colors[hash % colors.length];
    }

    const getAge = (dateOfBirth) => {
        const birthdate = new Date(dateOfBirth);
        if (isNaN(birthdate.getTime())) {
            return ''; 
        }
        const today = new Date();
        let age = today.getFullYear() - birthdate.getFullYear();
        const monthDifference = today.getMonth() - birthdate.getMonth();
        const dayDifference = today.getDate() - birthdate.getDate();
        if (monthDifference < 0 || (monthDifference === 0 && dayDifference < 0)) {
            age--;
        }
        return age;
    }

    const getFormattedAddress = (addresses) => {
        if (!addresses || addresses.length === 0) return "No address provided";

        const address = addresses[0]; // Get the first address
        const addressParts = [];

        if (address.addressLine) addressParts.push(address.addressLine);
        if (address.city) addressParts.push(address.city);
        if (address.state) addressParts.push(address.state);
        if (address.zipcode) addressParts.push(address.zipcode);
        if (address.country) addressParts.push(address.country);

        return addressParts.join(", ");
    }

    const handleEditDOBClick = () => {
        navigate("/profile-details/edit-dob");
    }
    const handleEditAddressClick = () => {
        navigate("/profile-details/edit-address");
    }
    const handleEditLanguageClick = () => {
        navigate("/profile-details/edit-language");
    }
    const handleEditGenderClick = () => {
        navigate("/profile-details/edit-gender");
    }
    const handleEditPoliticalAffiliationClick = () => {
        navigate("/profile-details/edit-political-affiliation");
    }
    const handleEditProfessionsClick = () => {
        navigate("/profile-details/edit-professions");
    }

    const isGovernmentIdVerified = userDetails?.governmentId?.isVerified === true;
    // console.log("isGovernmentIdVerified", isGovernmentIdVerified);


    return (
        <div className="BasicDetails">
            <div className="basicDetailsContainer">
                <ProfileCompletenessCard />
                <div className="imageContainer">
                    <div className="imageContainerText">
                        <span className="coverPhotoText">Cover Photo</span>
                        <span className="editText" onClick={handleEditClick}>Edit</span>
                    </div>
                    <div className="coverPhotoContainer">
                        {
                            userDetails?.photos && !imageError ? (
                                <img
                                    src={userDetails.photos}
                                    alt="Cover banner"
                                    className="coverPhoto"
                                    onError={() => setImageError(true)}
                                />
                            ) : (
                                <div
                                    className="coverPhoto"
                                    style={{
                                        backgroundColor: getBackgroundColor(getUserInitials(userDetails?.firstName, userDetails?.lastName)),
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "white",
                                        fontWeight: "bold",
                                        fontSize: "64px",
                                        fontFamily: "Arial, sans-serif"
                                    }}
                                >
                                    {getUserInitials(userDetails?.firstName, userDetails?.lastName)}
                                </div>
                            )
                        }
                    </div>
                </div>
                <div className="nameContainer">
                    <div className="nameTextContainer"><span className="nameText">Name</span>
                        <span className="nameValueText">{userDetails?.firstName} {userDetails?.middleName} {userDetails?.lastName}</span>
                    </div>
                    <div className="editIconContainer" onClick={handleEditNameClick}>
                        <EditRoundedIcon className="editIcon" />
                    </div>
                </div>
                <div className="nameContainer">
                    <div className="nameTextContainer">
                        <span className="nameText">When is your birthday?</span>
                        <span className="nameValueText">{(userDetails?.dateOfBirth ?? "").split("T")[0]}</span>
                    </div>
                    {!isGovernmentIdVerified && (
                        <div
                            className="editIconContainer"
                            onClick={handleEditDOBClick}
                            title="Edit DOB"
                        >
                            <EditRoundedIcon className="editIcon" />
                        </div>
                    )}
                </div>
                <div className="nameContainer">
                    <div className="nameTextContainer">
                        <span className="nameText">What's your age?</span>
                        <span className="nameValueText">{getAge(userDetails?.dateOfBirth)}</span>
                    </div>
                    <div></div>
                </div>
                <div className="nameContainer">
                    <div className="nameTextContainer"><span className="nameText">Where do you live?</span>
                        <span className="nameValueText">{getFormattedAddress(userDetails?.addresses)}</span>
                    </div>
                    <div className="editIconContainer" onClick={handleEditAddressClick}>
                        <EditRoundedIcon className="editIcon" />
                    </div>
                </div>
                <div className="nameContainer">
                    <div className="nameTextContainer"><span className="nameText">What's your gender?</span>
                        <span className="nameValueText">{userDetails?.gender}</span>
                    </div>
                    {!isGovernmentIdVerified && (
                        <div
                            className="editIconContainer"
                            onClick={handleEditGenderClick}
                            title="Edit Gender"
                        >
                            <EditRoundedIcon className="editIcon" />
                        </div>
                    )}
                </div>
                <div className="nameContainer">
                    <div className="nameTextContainer"><span className="nameText">What languages do you speak?</span>
                        <span className="nameValueText">{Array.isArray(userDetails?.languages) ? userDetails.languages.join(", ") : (userDetails?.languages ?? "")}</span>
                    </div>
                    <div className="editIconContainer" onClick={handleEditLanguageClick}>
                        <EditRoundedIcon className="editIcon" />
                    </div>
                </div>
                <div className="nameContainer">
                    <div className="nameTextContainer"><span className="nameText">Political affiliation</span>
                        <span className="nameValueText">{userDetails?.politicalAffiliation ? `I currently support the ${userDetails.politicalAffiliation}` : "Not specified"}</span>
                    </div>
                    <div className="editIconContainer" onClick={handleEditPoliticalAffiliationClick}>
                        <EditRoundedIcon className="editIcon" />
                    </div>
                </div>
                <div className="nameContainer">
                    <div className="nameTextContainer"><span className="nameText">Professions & Titles</span>
                        <span className="nameValueText">
                            {Array.isArray(userDetails?.professions) && userDetails.professions.length > 0
                                ? userDetails.professions.join(", ")
                                : "Not specified"}
                        </span>
                    </div>
                    <div className="editIconContainer" onClick={handleEditProfessionsClick} title="Edit Professions & Titles">
                        <EditRoundedIcon className="editIcon" />
                    </div>
                </div>

            </div>
        </div>
    );
}

export default BasicDetails;